import api from '../../services/api';

export interface Category { CategoryID: number; Code: string; Name: string; IsActive: boolean; SortOrder: number; }
export interface AccountMatch {
  AccountNumber: string; SchoolName?: string; StudentName?: string; SignerName?: string;
  StudentStatus?: string; CourseName?: string; ContractDate?: string; MatchedOn?: string;
}
export interface Call {
  CallID: number; ZoomCallId?: string; AgentID?: number; AgentName?: string; Direction?: string;
  CallerNumber?: string; CalleeNumber?: string; CallTimeUtc: string;
  CallStartTimeUtc?: string; CallAnswerTimeUtc?: string; DurationSeconds?: number;
  RecordingUrl?: string; AccountNumber?: string; SchoolName?: string; StudentName?: string;
  StudentStatus?: string; CourseName?: string; ContractDate?: string; MatchSource?: string;
  IsMissed: boolean;
  Notes?: string; Status: string; Source: string; Categories: Category[];
  ZoomUserId?: string;
  // FR-3.7 of docs/requirements/admin-calls-filters-and-transcript.md — the call's transcript
  // fetch state, reusing TranscriptCallFetchStatus (declared further down this file; TS type
  // aliases aren't order-sensitive) rather than redeclaring the same union. Optional because
  // not every getAllCalls caller/fixture necessarily sets it, even though the live endpoint
  // always includes it (defaulting to 'NotAttempted' server-side).
  TranscriptFetchStatus?: TranscriptCallFetchStatus;
}
// Only the account link + agent-entered annotations are editable; account details are derived.
export interface CallInput {
  AccountNumber?: string; MatchSource?: string; Notes?: string;
  Status: string; CallTimeUtc?: string; CallerNumber?: string; CategoryIds: number[];
}
export interface AgentAnsweredStat { AgentID: number; DisplayName?: string; AnsweredCount: number; AvgDurationSeconds?: number | null; }
export interface MissedByHourStat { Hour: number; MissedCount: number; }
export interface DailyCallStat { Date: string; AnsweredCount: number; MissedCount: number; }
export interface RecentMissedCall {
  CallID: number; ZoomCallId?: string; CallerNumber?: string; CallTimeUtc: string;
  AccountNumber?: string; SchoolName?: string; StudentName?: string;
}
// A call can carry multiple categories, so category counts sum to assignments, not calls.
export interface CategoryCallStat { CategoryID: number; Code: string; Name: string; CallCount: number; }
// FR Group 2/3 of docs/requirements/metrics-overflow-tracking.md. AnsweredCount/MissedCount
// KEEP their names but CHANGE MEANING (FR-3.1): MissedCount now includes every overflow call
// unconditional on time of day; AnsweredCount excludes them. Total = Answered + Missed still
// holds. The four BusinessHours*/Overflow* fields are all null together when
// BusinessHoursConfigured is false (FR-2.6/FR-3.6 "not yet configured" states) — never a
// silent zero or a fallback to an unscoped rate. OverflowCoveredCount is always present
// (never null) and pairs with Answered+Missed to derive a coverage % for the two Overflow
// cards, mirroring the AvgDurationSeconds coverage precedent (FR-2.4). It counts rows whose
// OVERFLOW STATUS has been determined (Call.IsOverflow IS NOT NULL) — deliberately not rows
// with a known raw ZoomCallResult, which is a misleading proxy: overflow is only visible in
// the per-call call_path detail fetch (FR-1.4, corrected 2026-08-26), so a call can have a
// known CallResult while its overflow status is still unknown. Coverage < 100% means the two
// Overflow counts may currently under-report for the range.
export interface MissedCallMetrics {
  AnsweredCount: number; MissedCount: number;
  AvgDurationSeconds?: number | null; // null until upstream duration backfill (R-A1)
  BusinessHoursConfigured: boolean;
  OverflowBusinessHoursCount: number | null;
  OverflowAfterHoursCount: number | null;
  BusinessHoursAnsweredCount: number | null;
  BusinessHoursMissedCount: number | null;
  OverflowCoveredCount: number;
  PerAgent: AgentAnsweredStat[]; MissedByHour: MissedByHourStat[]; RecentMissed: RecentMissedCall[];
  ByDay: DailyCallStat[]; ByCategory: CategoryCallStat[];
}
export interface Agent {
  AgentID: number; AzureADObjectID?: string; ZoomUserId?: string; ZoomEmail?: string; IsActive: boolean;
  DisplayName?: string; Email?: string; ZoomDisplayName?: string;
  JcsaEmployeeID?: number; JcsaEmployeeUnassigned?: boolean;
  MatchedJcsaEmployeeID?: number; MatchedJcsaEmployeeName?: string;
  // Workforce.Roster identity mapping (Call Insights Reports 2 & 3 — schedule/punch join).
  // RosterCanonicalName is the persisted manual override (or explicit null once cleared);
  // MatchedRosterName is a best-effort auto-match suggestion, same idea as MatchedJcsaEmployeeID.
  RosterCanonicalName?: string | null;
  MatchedRosterName?: string | null;
}
export interface JcsaEmployee { EmployeeID: number; Name?: string; Email?: string; }
export interface CurrentAgent { AgentID?: number; IsMapped: boolean; IsAdmin: boolean; DisplayName: string; Environment: string; }
export interface ZoomUser { UserId: string; Email?: string; DisplayName?: string; }
// One weekday's business-hours window (docs/requirements/metrics-overflow-tracking.md FR Group
// 4, amended 2026-08-25 per Ames: "WE need each day having different business hours" —
// per-day hours, superseding the doc's original single-window FR Group 4). Weekday numbering
// matches GetWeeklyCoverage/RecommendedStaffing elsewhere in this feature: 1=Sunday..7=Saturday.
// OpenEt/CloseEt are both null when that day is deliberately closed (no invented defaults —
// every day starts closed until an admin sets it, FR-4.2).
export interface BusinessHoursDay {
  Weekday: number; // 1=Sun..7=Sat
  OpenEt: string | null; // "HH:mm" 24-hour ET
  CloseEt: string | null;
}
export interface CommunicationSettings {
  ZoomCallQueueExtension?: string;
  ZoomCallQueueExtensionSource: 'Database' | 'Config' | 'Unset';
  UpdatedUtc?: string;
  UpdatedBy?: string;
  // Zoom API Sweep (see docs/requirements/zoom-api-sweep.md §6). SweepEnabled is a
  // server-config-only feature gate — display only, never sent on update.
  SweepEnabled: boolean;
  SweepIntervalMinutes: number;
  SweepLookbackHours: number;
  // Business hours — always exactly 7 entries, Weekday 1..7 ascending. BusinessHoursConfigured
  // (also mirrored onto MissedCallMetrics) is true iff at least one day has a complete
  // open/close pair; that's what drives the Metrics tab's "not yet configured" states.
  BusinessHours: BusinessHoursDay[];
  BusinessHoursConfigured: boolean;
}
// One sweep run — routine/scheduled sweep or an admin-triggered historical backfill. A
// backfill's POST response and every subsequent /status poll share this same shape: 'Running'
// while the (possibly multi-chunk) backfill is still processing server-side (FinishedUtc null,
// counts 0/null), settling into a terminal status (OK/PartialError/Failed) with the aggregate
// counts across all chunks once it finishes.
export interface SweepRun {
  SweepRunId: number;
  StartedUtc: string;
  FinishedUtc: string | null;
  QueueIdResolved: string | null;
  CallsSeen: number;
  CallsCreated: number;
  CallsBackfilled: number;
  Errors: number;
  ErrorSummary: string | null;
  Status: 'OK' | 'PartialError' | 'Failed' | 'Running';
}
export interface ZoomQueuePreview {
  Found: boolean; Extension: string; QueueId?: string; QueueName?: string; MemberCount: number;
}
// One Sunday-start week of Workforce hours/FTE metrics (CS Cost Reduction project).
export interface WeeklyMetrics {
  WeekStartDate: string; WeekEndDate: string;
  ScheduledHours: number; WorkedHours: number; AnsweringRoleWorkedHours: number;
  RecommendedHours: number; ScheduledFTE: number; WorkedFTE: number; AnsweringRoleWorkedFTE: number;
  BaselineHours: number; BaselineFTE: number;
  WorkedVsBaselinePct: number; WorkedVsRecommendedHours: number;
}
export interface RosterMember {
  RosterID: number; CanonicalName: string; Role: string; TimeZone: string;
  IsActive: boolean; LoadedAtUtc: string;
}
// One day x 15-minute interval of the weekly coverage grid (all times ET).
// Scheduled/Actual counts are Answering-role only; the "Other" counts (Supervisors /
// OtherProject) are shown alongside as context but never drive the cell coloring.
export interface CoverageSlot {
  Date: string; DayOffset: number; IntervalStartET: string;
  RequiredAgents: number; ScheduledAgents: number; ActualAgents: number;
  ScheduledOtherAgents: number; ActualOtherAgents: number;
}

// One recurring off-call block for an Answering-role agent — one row per (agent, weekday).
// Times are ET, 'HH:mm'; EndTimeET <= StartTimeET means an overnight wrap (mirrors
// ScheduleShift). EffectiveTo null = still in effect (open-ended).
export interface CoverageException {
  ExceptionID: number;
  AgentName: string;
  Weekday: number; // 1=Sun..7=Sat, matching RecommendedStaffing/GetWeeklyCoverage
  StartTimeET: string;
  EndTimeET: string;
  Label: string;
  EffectiveFrom: string; // YYYY-MM-DD
  EffectiveTo: string | null; // YYYY-MM-DD
  CreatedBy: string;
  CreatedAtUtc: string;
}
// Create request: one row per weekday in Weekdays is created server-side.
export interface CoverageExceptionInput {
  AgentName: string;
  Weekdays: number[];
  StartTimeET: string;
  EndTimeET: string;
  Label: string;
  EffectiveFrom: string;
  EffectiveTo?: string;
}

// Result of a single MakeShift schedule / ADP payroll file upload. Files may span any
// date range (not just one week), so the parsed span is reported as rangeStart/rangeEnd.
export type UploadFileType = 'schedule' | 'payroll';
export interface UploadResult {
  fileType: UploadFileType;
  fileName: string;
  rangeStart: string; // YYYY-MM-DD
  rangeEnd: string; // YYYY-MM-DD
  rowsInserted: number;
  rowsSkipped: number;
  skippedNames: string[];
  replacedExisting: boolean;
}
// ── Call Insights (Communication → Admin → Insights) ──────────────────────────
// FR Groups 1-4 of docs/requirements/metrics-call-insights.md. All ranges are US Eastern
// calendar dates (YYYY-MM-DD, inclusive); every talk-time figure excludes NULL-duration
// calls from both numerator and denominator (never treats a NULL as zero) and pairs with
// a coverage count/percentage so the UI can show "how much of this average is really there."

// One 15-minute ET bucket of the intraday volume report (FR Group 1).
//
// IMPORTANT — Calls is the ANSWERED count, NOT a total. It mirrors the backend's
// InsightsModels.cs IntradayBucket.Calls (SUM(CASE WHEN r.IsAnswered = 1 ...)), i.e. the
// green/answered series. Missed is the separate unanswered count (the red series). The
// bucket's total call volume is Calls + Missed — there is no field that is the sum.
// (Property kept as `Calls` — not renamed to `Answered` — because it must match the wire
// name Dapper/System.Text.Json serialize from the backend's `Calls` property.)
export interface IntradayVolumeBucket { IntervalStartET: string; Calls: number; Missed: number; }
// Buckets are summed across every day in the range; Days reports how many were summed so
// the UI can foot the chart "Summed across N days" (FR-1.5 / OQ-4).
export interface IntradayVolumeResponse { Days: number; Buckets: IntradayVolumeBucket[]; }

export interface RepTimelineCall { CallID: number; StartET: string; DurationSeconds: number | null; }
export interface RepTimelinePunch { StartET: string; EndET: string; }
export interface RepTimelineShift { StartET: string; EndET: string; }
export interface RepTimelineRep {
  AgentID: number; DisplayName: string; RosterCanonicalName: string | null;
  Calls: RepTimelineCall[]; Punches: RepTimelinePunch[]; Shifts: RepTimelineShift[];
}
// One ET calendar day (FR Group 2 — single-day view only). Reps not resolvable to a Roster
// mapping are listed by name only (no schedule/punch layers) in UnmappedAgentNames.
export interface RepTimelineResponse { Date: string; Reps: RepTimelineRep[]; UnmappedAgentNames: string[]; }

// One rep's aggregate row for the selected range (FR Group 3). Answered-calls-only (OQ-5).
// Scheduled/Exception/Net/TalkVsScheduled are null (never 0) when the rep has no Roster
// mapping — FR-3.2 requires those show as unavailable, not a misleading zero.
export interface RepProductivityRow {
  AgentID: number; DisplayName?: string; RosterCanonicalName: string | null;
  AnsweredCalls: number; CallsWithDuration: number; TotalTalkSeconds: number;
  AvgTalkSeconds: number | null; DurationCoveragePct: number;
  ScheduledHours: number | null; ExceptionHours: number | null;
  NetScheduledHours: number | null; TalkVsScheduledPct: number | null;
}

// One agent's average talk time, either overall or within one category breakdown
// (FR Group 4). CallCount is the sample size shown as "(n=X)" — no suppression (OQ-8).
export interface TalkCompareRow { AgentID: number; DisplayName?: string; AvgTalkSeconds: number | null; CallCount: number; }
export interface TalkCompareCategory { CategoryID: number; CategoryName: string; Rows: TalkCompareRow[]; }
export interface TalkTimeComparisonResponse { Overall: TalkCompareRow[]; ByCategory: TalkCompareCategory[]; }

// categoryIds: OR-filter (calls carrying at least one selected category); omit/empty = no
// filter. Per the existing "Calls by category" convention, a multi-category call counts
// once per category within a category breakdown — every such view foots that explicitly.
export const getInsightsIntraday = (from: string, to: string, categoryIds?: number[]) =>
  api.get<IntradayVolumeResponse>('/communication/admin/insights/intraday', {
    params: { from, to, categoryIds: categoryIds && categoryIds.length > 0 ? categoryIds.join(',') : undefined },
  }).then(r => r.data);
export const getRepTimeline = (date: string) =>
  api.get<RepTimelineResponse>('/communication/admin/insights/rep-timeline', { params: { date } }).then(r => r.data);
export const getInsightsProductivity = (from: string, to: string) =>
  api.get<RepProductivityRow[]>('/communication/admin/insights/productivity', { params: { from, to } }).then(r => r.data);
export const getTalkCompare = (from: string, to: string, categoryIds?: number[]) =>
  api.get<TalkTimeComparisonResponse>('/communication/admin/insights/talk-compare', {
    params: { from, to, categoryIds: categoryIds && categoryIds.length > 0 ? categoryIds.join(',') : undefined },
  }).then(r => r.data);
// Sets (or, with null, explicitly clears) an agent's Workforce.Roster mapping. Mirrors
// setAgentJcsaEmployee's shape/error contract (400 { error } surfaced verbatim by callers).
export const setAgentRoster = (agentId: number, rosterCanonicalName: string | null) =>
  api.post<Agent>('/communication/admin/agents/' + agentId + '/roster', { RosterCanonicalName: rosterCanonicalName }).then(r => r.data);

// One row of the Workforce upload audit log (Workforce.UploadLog).
export interface UploadLogEntry {
  uploadedAtUtc: string;
  uploadedBy: string;
  fileType: string;
  fileName: string;
  rangeStart: string;
  rangeEnd: string;
  rowsInserted: number;
  rowsSkipped: number;
  skippedNames: string[];
  status: 'Success' | 'PartialSuccess' | 'Failed';
  errorSummary: string | null;
}

export const getMe = () => api.get<CurrentAgent>('/communication/me').then(r => r.data);
export const getCategories = () => api.get<Category[]>('/communication/categories').then(r => r.data);
export const getMyCalls = (status?: string) =>
  api.get<Call[]>('/communication/calls', { params: { status } }).then(r => r.data);
export const getCall = (id: number) => api.get<Call>(`/communication/calls/${id}`).then(r => r.data);
export const createCall = (input: CallInput) =>
  api.post<{ callId: number }>('/communication/calls', input).then(r => r.data);
export const updateCall = (id: number, input: CallInput) =>
  api.put(`/communication/calls/${id}`, input).then(r => r.data);
export const lookupAccount = (accountNumber: string) =>
  api.get<AccountMatch | null>('/communication/account-lookup', { params: { accountNumber } }).then(r => r.data);
export const lookupByPhone = (phone: string) =>
  api.get<AccountMatch[]>('/communication/account-lookup/by-phone', { params: { phone } }).then(r => r.data);

export const getAgents = () => api.get<Agent[]>('/communication/admin/agents').then(r => r.data);
export const deactivateAgent = (id: number) => api.delete(`/communication/admin/agents/${id}`).then(r => r.data);
export const reactivateAgent = (id: number) => api.post(`/communication/admin/agents/${id}/reactivate`).then(r => r.data);
// FR-3.6 — a single filter object (rather than a growing positional-argument list) for the
// All Calls card's Date Range/Agent/Account/Status filters (docs/requirements/
// admin-calls-filters-and-transcript.md). from/to are inclusive US Eastern calendar dates
// (YYYY-MM-DD); the server converts to UTC bounds and 400s if to < from. agentId and
// unassignedOnly are mutually exclusive from the UI's perspective (the Agent select only ever
// sends one), but both may be safely omitted together (no agent filter). accountNumber does a
// server-side prefix match (OQ-3) and is trimmed server-side. Axios drops any field left
// `undefined`, so callers only need to set the fields that are actually active.
export interface CallsFilter {
  status?: string;
  from?: string;
  to?: string;
  agentId?: number;
  unassignedOnly?: boolean;
  accountNumber?: string;
}
export const getAllCalls = (filter?: CallsFilter) =>
  api.get<Call[]>('/communication/admin/calls', { params: filter }).then(r => r.data);
export const getUnmappedCalls = () => api.get<Call[]>('/communication/admin/unmapped-calls').then(r => r.data);
// from/to are inclusive US Eastern calendar dates (YYYY-MM-DD); the API converts to UTC.
export const getMissedMetrics = (from: string, to: string) =>
  api.get<MissedCallMetrics>('/communication/admin/missed-metrics', { params: { from, to } }).then(r => r.data);
export const upsertCategory = (c: Partial<Category>) =>
  api.post<Category>('/communication/admin/categories', c).then(r => r.data);
export const deactivateCategory = (id: number) => api.delete(`/communication/admin/categories/${id}`).then(r => r.data);

export const getZoomUsers = () => api.get<ZoomUser[]>('/communication/admin/zoom-users').then(r => r.data);
export const setAgentZoom = (agentId: number, body: { ZoomUserId?: string; ZoomEmail?: string; ZoomDisplayName?: string }) =>
  api.post<Agent>('/communication/admin/agents/' + agentId + '/zoom', body).then(r => r.data);
export const setAgentJcsaEmployee = (agentId: number, jcsaEmployeeId: number | null, unassigned = false) =>
  api.post<Agent>('/communication/admin/agents/' + agentId + '/jcsa-employee', { JcsaEmployeeID: jcsaEmployeeId, Unassigned: unassigned }).then(r => r.data);
export const getJcsaEmployees = () =>
  api.get<JcsaEmployee[]>('/communication/admin/jcsa-employees').then(r => r.data);
export const assignCall = (callId: number, agentId: number) =>
  api.post('/communication/admin/calls/' + callId + '/assign', { AgentId: agentId }).then(r => r.data);
export const simulateCall = (body: { CallerNumber?: string; ZoomUserId?: string; Missed?: boolean }) =>
  api.post('/communication/dev/simulate-call', body).then(r => r.data);

export const getTimeTrackingWeeklyMetrics = (weeks = 26) =>
  api.get<WeeklyMetrics[]>('/communication/admin/timetracking/weekly-metrics', { params: { weeks } }).then(r => r.data);
export const getTimeTrackingRoster = () =>
  api.get<RosterMember[]>('/communication/admin/timetracking/roster').then(r => r.data);
// weekStart: any ET calendar date (YYYY-MM-DD); the API snaps to that week's Sunday.
export const getTimeTrackingCoverage = (weekStart: string) =>
  api.get<CoverageSlot[]>('/communication/admin/timetracking/weekly-coverage', { params: { weekStart } }).then(r => r.data);

// Uploads a MakeShift weekly schedule export (.xlsx) — writes Workforce.ScheduleShift.
// The api instance's default Content-Type (application/json) would override axios's
// FormData detection and the backend's multipart-only endpoint would 404, so each
// upload names multipart explicitly — axios replaces it with the boundary-qualified value.
export const uploadScheduleFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<UploadResult>('/communication/admin/timetracking/uploads/schedule', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
// Uploads an ADP payroll/timecard export (.xls or .xlsx) — writes Workforce.WorkedPunch.
export const uploadPayrollFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<UploadResult>('/communication/admin/timetracking/uploads/payroll', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const getUploadLog = (top = 20) =>
  api.get<UploadLogEntry[]>('/communication/admin/timetracking/uploads/log', { params: { top } }).then(r => r.data);

// Off-call exceptions (Workforce.CoverageException) — CRUD for recurring blocks that
// exclude an Answering-role agent from the coverage grid's ScheduledAgents count.
export const getCoverageExceptions = () =>
  api.get<CoverageException[]>('/communication/admin/timetracking/exceptions').then(r => r.data);
// Creates one row per Weekdays entry; the server rejects the whole batch (400 { error })
// if any weekday overlaps an existing active exception for the same agent.
export const createCoverageException = (input: CoverageExceptionInput) =>
  api.post<CoverageException[]>('/communication/admin/timetracking/exceptions', input).then(r => r.data);
export const endCoverageException = (id: number, effectiveTo: string) =>
  api.post(`/communication/admin/timetracking/exceptions/${id}/end`, { EffectiveTo: effectiveTo }).then(r => r.data);
export const deleteCoverageException = (id: number) =>
  api.delete(`/communication/admin/timetracking/exceptions/${id}`).then(r => r.data);

export const getSettings = () =>
  api.get<CommunicationSettings>('/communication/admin/settings').then(r => r.data);
// Omit BusinessHours to leave the current week unchanged. When sent, it must be a full
// replacement of all 7 days (Weekday 1..7) — not a partial patch. The server 400s per-day
// (naming the offending weekday) on a malformed time or close <= open (FR-4.5).
export const updateSettings = (body: {
  ZoomCallQueueExtension?: string;
  SweepIntervalMinutes?: number;
  SweepLookbackHours?: number;
  BusinessHours?: BusinessHoursDay[];
}) =>
  api.put<CommunicationSettings>('/communication/admin/settings', body).then(r => r.data);
export const previewZoomQueue = (extension: string) =>
  api.get<ZoomQueuePreview>('/communication/admin/settings/zoom-queue-preview', { params: { extension } }).then(r => r.data);

// Most recent sweep run (routine or backfill) — 'Running' while still in progress server-side,
// or null if none has ever recorded (Communication.GetLastSweepRun returns no row on a fresh
// environment). Poll this to track a backfill through to completion (see runSweepBackfill).
export const getSweepStatus = () =>
  api.get<SweepRun | null>('/communication/admin/sweep/status').then(r => r.data);
// Admin-triggered historical backfill over an arbitrary date range (yyyy-MM-dd, inclusive).
// Async: the server chunks internally (Zoom's one-month-per-request limit) and returns 202
// immediately with a SweepRun-shaped body whose Status is 'Running' (FinishedUtc null, counts
// 0/null) — the run continues server-side after the response. Poll getSweepStatus() to observe
// it finalize (OK/PartialError/Failed). No timeout override needed: the POST itself returns
// fast now, so the api instance's default applies. 400 on an invalid range, 409 if a run is
// already in progress (both carry { error } and are surfaced to the admin verbatim).
export const runSweepBackfill = (from: string, to: string) =>
  api.post<SweepRun>('/communication/admin/sweep/run', { From: from, To: to }).then(r => r.data);

// ── Call Transcript Retrieval (docs/requirements/call-transcript-retrieval.md) ──────────────
// CommunicationAdmin-only. The doc's "blocked, pending Zoom scope grant" status is stale as of
// this frontend work — the scope was granted and OQ-5 is resolved in favor of a minimal
// read-only viewer shipping in v1 (see FR Group 2/5/6 and the Component Scope section).

// FR Group 2's reusable selection filter, shared by the preview/fetch-trigger/list/export
// endpoints so a preview count, a triggered run, and an export always agree on the same set of
// calls. from/to are inclusive US Eastern calendar dates (YYYY-MM-DD). No selection default is
// baked in here — see DEFAULT_TRANSCRIPT_FILTER in TranscriptRetrievalPanel.tsx for the FR-2.2
// 829-call reproduction values.
export interface TranscriptFilter {
  categoryIds: number[];
  from: string;
  to: string;
  answeredOnly: boolean;
  soloCategoryOnly: boolean;
  requireAgent: boolean;
}
const transcriptFilterParams = (f: TranscriptFilter): Record<string, string | boolean> => ({
  categoryIds: f.categoryIds.join(','),
  from: f.from,
  to: f.to,
  answeredOnly: f.answeredOnly,
  soloCategoryOnly: f.soloCategoryOnly,
  requireAgent: f.requireAgent,
});

export interface TranscriptPreview {
  MatchingCalls: number;
  WithRecording: number;
  Fetched: number;
  Pending: number;
}
export const getTranscriptPreview = (filter: TranscriptFilter) =>
  api.get<TranscriptPreview>('/communication/admin/transcripts/preview', {
    params: transcriptFilterParams(filter),
  }).then(r => r.data);

// The trigger POST's 202 body is lean by contract — just the new run's id. Callers fetch full
// status via getTranscriptFetchStatus() immediately after (mirroring the sweep backfill's
// pattern, but the transcript trigger doesn't echo the full run shape itself).
export interface TranscriptFetchTrigger { RunId: number; }
export const runTranscriptFetch = (filter: TranscriptFilter) =>
  api.post<TranscriptFetchTrigger>('/communication/admin/transcripts/fetch', {
    CategoryIds: filter.categoryIds,
    From: filter.from,
    To: filter.to,
    AnsweredOnly: filter.answeredOnly,
    SoloCategoryOnly: filter.soloCategoryOnly,
    RequireAgent: filter.requireAgent,
  }).then(r => r.data);

export type TranscriptRunStatus = 'Running' | 'OK' | 'PartialError' | 'Failed' | 'Blocked';
// One transcript-fetch run's terminal/in-progress counts (FR-4.4/FR-4.5) — NoRecording and
// NoTranscriptAvailable are expected, non-error outcomes; Blocked is the missing-Zoom-scope
// case (FR-1.3) and must read as distinct from Failed in the UI.
export interface TranscriptFetchRun {
  RunId: number;
  StartedUtc: string;
  FinishedUtc: string | null;
  Status: TranscriptRunStatus;
  Targeted: number;
  Fetched: number;
  NoRecording: number;
  NoTranscriptAvailable: number;
  Blocked: number;
  Failed: number;
  ErrorSummary: string | null;
}
// null = no transcript-fetch run has ever executed in this environment.
export const getTranscriptFetchStatus = () =>
  api.get<TranscriptFetchRun | null>('/communication/admin/transcripts/fetch/status').then(r => r.data);

export type TranscriptCallFetchStatus =
  | 'NotAttempted' | 'Fetched' | 'NoRecording' | 'NoTranscriptAvailable' | 'Blocked' | 'Failed';
// One row of the transcript-retrieval results list (FR Group 6 / admin spot-check view).
export interface TranscriptCallSummary {
  CallID: number;
  ZoomCallId: string;
  CallTimeUtc: string;
  DurationSeconds: number | null;
  AgentID: number | null;
  AgentName: string | null;
  CategoryIDs: number[];
  FetchStatus: TranscriptCallFetchStatus;
  SegmentCount: number | null;
  FetchedUtc: string | null;
}
// Newest first.
export const getTranscriptCalls = (filter: TranscriptFilter) =>
  api.get<TranscriptCallSummary[]>('/communication/admin/transcripts', {
    params: transcriptFilterParams(filter),
  }).then(r => r.data);

export type TranscriptSpeakerRole = 'Agent' | 'Caller';
// One speaker-attributed, timestamped turn. StartTs/EndTs are "HH:MM:SS.mmm" relative to
// recording start — not wall-clock/UTC.
export interface TranscriptSegment {
  StartTs: string;
  EndTs: string;
  SpeakerName: string;
  SpeakerRole: TranscriptSpeakerRole;
  Text: string;
}
// One call's full transcript detail (spot-check viewer). LastError/Segments/TranscriptText are
// populated only when relevant to FetchStatus — a non-Fetched call still returns a well-formed
// object (LastError set on Blocked/Failed, everything transcript-shaped left empty) so the
// viewer never has to guess at a missing field.
export interface TranscriptDetail {
  CallID: number;
  ZoomCallId: string;
  CallTimeUtc: string;
  DurationSeconds: number | null;
  AgentID: number | null;
  AgentName: string | null;
  CategoryIDs: number[];
  FetchStatus: TranscriptCallFetchStatus;
  LastError: string | null;
  RecordingStartUtc: string | null;
  RecordingEndUtc: string | null;
  Segments: TranscriptSegment[];
  TranscriptText: string | null;
}
export const getTranscriptDetail = (callId: number) =>
  api.get<TranscriptDetail>(`/communication/admin/transcripts/${callId}`).then(r => r.data);

// Authenticated blob download — the app's Bearer token lives only in the axios interceptor, so
// a plain <a href> would 401; this fetches via the shared `api` instance with
// responseType: 'blob' and triggers the save client-side. Mirrors useCaapApi.ts's downloadBlob
// (the repo's existing precedent for this exact problem), with an added `params` argument since
// export takes the FR Group 2 filter as query params rather than a path segment.
async function downloadBlob(
  url: string,
  params: Record<string, string | boolean>,
  fallbackFilename: string,
): Promise<void> {
  const response = await api.get<Blob>(url, { params, responseType: 'blob' });
  const filename = parseContentDispositionFilename(
    response.headers['content-disposition'] as string | undefined,
  ) ?? fallbackFilename;

  const objectUrl = window.URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}
function parseContentDispositionFilename(header: string | undefined): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch ? plainMatch[1] : null;
}
export const exportTranscripts = (filter: TranscriptFilter, format: 'json' | 'csv') =>
  downloadBlob(
    '/communication/admin/transcripts/export',
    { ...transcriptFilterParams(filter), format },
    `call-transcripts_${filter.from}_to_${filter.to}.${format}`,
  );
